import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getPageTitle } from "../../utils/getPageTitle";
import ApplicationImagesData from "../../components/applicationImageData";
import ApplicationImageStatusModal from "../../components/modal/applicationImageStatusModal";
import { useViewApplicationImages } from "../../hooks/applicationImages/applicationImages.js";
import { useUpdateApplicationImageStatus } from "../../hooks/applicationImages/applicationImages.updateStatus.js";
import { getApplicationImageStatus } from "../../utils/applicationImageStatus";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import {
  getPlanLimitMessage,
  getPlanMediaLimits,
} from "../subscription/planLimits";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";
import "../../assets/styles/button.css";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const ApplicationImages = () => {
  const location = useLocation();
  const [localImages, setLocalImages] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const {
    data: imagesResponse,
    isLoading,
    isFetching,
    isError,
  } = useViewApplicationImages({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
    status: statusFilter,
  });
  const { mutate: updateStatus, isPending: isUpdatingStatus } =
    useUpdateApplicationImageStatus();
  const { data: subscriptionResponse, isPlaceholderData } =
    useViewSubscription();
  const planLimits = getPlanMediaLimits(subscriptionResponse, {
    isReady: !isPlaceholderData,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!imagesResponse) return;
    setLocalImages(imagesResponse.applicationImages ?? []);
    const apiPagination = imagesResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [imagesResponse, pageSize]);

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, statusFilter]);

  const canApproveMore = Boolean(planLimits.applicationImages?.canApprove);
  const currentSelectedStatus = selectedImage
    ? getApplicationImageStatus(selectedImage)
    : "pending";
  const canApproveSelected =
    canApproveMore || currentSelectedStatus === "approved";

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const handleEdit = (row) => {
    setSelectedImage(row);
    setIsModalOpen(true);
  };

  const applyLocalStatus = (id, status) => {
    setLocalImages((prev) =>
      prev.map((img) =>
        String(img?._id || img?.id) === String(id)
          ? { ...img, status }
          : img,
      ),
    );
  };

  const handleApproveRow = (row) => {
    const id = row?._id || row?.id;
    if (!id) {
      toast.error("Image ID not found");
      return;
    }
    if (getApplicationImageStatus(row) === "approved") return;
    if (!planLimits.applicationImages?.canApprove) {
      toast.error(getPlanLimitMessage("applicationImages", planLimits));
      return;
    }
    updateStatus(
      { id, status: "approved" },
      { onSuccess: () => applyLocalStatus(id, "approved") },
    );
  };

  const handleSetPending = (row) => {
    const id = row?._id || row?.id;
    if (!id) {
      toast.error("Image ID not found");
      return;
    }
    if (getApplicationImageStatus(row) === "pending") return;
    updateStatus(
      { id, status: "pending" },
      {
        onSuccess: () => applyLocalStatus(id, "pending"),
      },
    );
  };

  const handleSaveStatus = (newStatus, rejectReason) => {
    const id = selectedImage?._id || selectedImage?.id;
    if (!id) {
      toast.error("Image ID not found");
      return;
    }

    const currentStatus = getApplicationImageStatus(selectedImage);

    if (newStatus === "rejected" && !String(rejectReason || "").trim()) {
      toast.error("Please provide a rejection reason.");
      return;
    }

    if (newStatus === currentStatus && newStatus !== "rejected") {
      closeModal();
      return;
    }

    if (newStatus === "approved" && currentStatus !== "approved") {
      if (!planLimits.applicationImages?.canApprove) {
        toast.error(getPlanLimitMessage("applicationImages", planLimits));
        return;
      }
    }

    updateStatus(
      { id, status: newStatus, rejectReason },
      {
        onSuccess: () => applyLocalStatus(id, newStatus),
        onSettled: () => closeModal(),
      },
    );
  };

  if (isLoading) return <div>Loading application images...</div>;
  if (isError) return <div>Error loading application images</div>;

  return (
    <>
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p style={{ color: "rgb(85, 85, 85)", fontSize: "13px" }}>
            View and edit pending, approved, and rejected submissions. Only
            approved images count toward your plan.
          </p>
        </div>
        <p
          className={`plan-limit-hint${
            canApproveMore ? "" : " is-limit"
          }`}
        >
          {getPlanLimitMessage("applicationImages", planLimits)}
        </p>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localImages}
            searchField={["title", "name", "email", "status"]}
            placeholder="Search application images..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => setSearchTerm(searchItem)}
          />
          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
        <div className="list-toolbar-filters" role="tablist" aria-label="Filter by status">
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type="button"
              role="tab"
              aria-selected={statusFilter === filter.value}
              className={`status-filter-chip${
                statusFilter === filter.value ? " active" : ""
              }`}
              onClick={() => setStatusFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <ApplicationImageStatusModal
        isOpen={isModalOpen}
        onClose={closeModal}
        image={selectedImage}
        onSave={handleSaveStatus}
        isLoading={isUpdatingStatus}
        canApprove={canApproveSelected}
        approveLimitMessage={getPlanLimitMessage(
          "applicationImages",
          planLimits,
        )}
      />

      <ApplicationImagesData
        data={localImages}
        onEdit={handleEdit}
        onPending={handleSetPending}
        onApprove={handleApproveRow}
        canApprove={canApproveMore}
      />

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default ApplicationImages;
